import { useHandler } from "@redotech/react-util/hook";
import { AssistantMessageContent } from "@redotech/redo-model/agents/llm-messages";
import { MessageAuthorInfo } from "@redotech/redo-web/ai/chat/contexts/message-author-context";
import { MutableRefObject, useState } from "react";
import { useInChatEscalationFlag } from "./in-chat-escalation-flag-provider";
import {
  useEscalationSubscription,
  type HumanMessage,
} from "./use-escalation-subscription";

const INTERCOM_ATTACHMENT_PLACEHOLDER_REGEX =
  /\[(?:attachment|image|video)\s*:?\s*"[^"]*"\]/gi;
const INTERCOM_SIMPLE_MEDIA_PLACEHOLDER_REGEX =
  /\[(?:gif|image|video|attachment)\]/gi;
const INTERCOM_HTML_BREAK_REGEX = /<(?:br\s*\/?|\/p|\/div|\/li)>/gi;
const INTERCOM_HTML_LIST_ITEM_OPEN_REGEX = /<li[^>]*>/gi;
const INTERCOM_HTML_TAG_REGEX = /<[^>]+>/g;

function decodeHtmlEntities(text: string): string {
  return text
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
}

export function sanitizeIntercomBody(body: string | null | undefined): string {
  if (!body) return "";
  const stripped = body
    .replace(INTERCOM_HTML_BREAK_REGEX, "\n")
    .replace(INTERCOM_HTML_LIST_ITEM_OPEN_REGEX, "- ")
    .replace(INTERCOM_HTML_TAG_REGEX, "")
    .replace(/\n{2,}/g, "\n")
    .trim()
    .replace(/\n/g, " ")
    .replace(INTERCOM_ATTACHMENT_PLACEHOLDER_REGEX, "")
    .replace(INTERCOM_SIMPLE_MEDIA_PLACEHOLDER_REGEX, "");
  return decodeHtmlEntities(stripped).replace(/\s+/g, " ").trim();
}

interface UseHumanMessageHandlerParams {
  addMessage: (message: {
    id: string;
    role: "assistant";
    content: string | AssistantMessageContent[];
  }) => void;
  processedMessageIdsRef: MutableRefObject<Set<string>>;
  authorRegistryRef: MutableRefObject<Map<string, MessageAuthorInfo>>;
  isOpenRef: MutableRefObject<boolean>;
  onClosedPanelMessage?: (message: HumanMessage) => void;
}

function buildAttachmentContent(
  message: HumanMessage,
): AssistantMessageContent[] {
  const contentParts: AssistantMessageContent[] = [];

  const cleanedBody = sanitizeIntercomBody(message.body);
  if (cleanedBody) {
    contentParts.push({ type: "text" as const, text: cleanedBody });
  }

  for (const attachment of message.attachments!) {
    const isImage = attachment.contentType?.startsWith("image/");
    const isVideo = attachment.contentType?.startsWith("video/");

    const attachmentData = JSON.stringify({
      url: attachment.url,
      type: attachment.contentType,
      name:
        isImage || isVideo ? attachment.name : attachment.name || "Attachment",
    });

    const marker = isImage
      ? "image-data"
      : isVideo
        ? "video-data"
        : "document-data";

    contentParts.push({
      type: "text" as const,
      text: `[[[[${marker}:${attachmentData}]]]]`,
    });
  }

  return contentParts;
}

export function useHumanMessageHandler({
  addMessage,
  processedMessageIdsRef,
  authorRegistryRef,
  isOpenRef,
  onClosedPanelMessage,
}: UseHumanMessageHandlerParams) {
  const inChatEscalation = useInChatEscalationFlag();
  const [agentName, setAgentName] = useState<string | null>(null);
  const [agentAvatarUrl, setAgentAvatarUrl] = useState<string | null>(null);

  const handleHumanMessage = useHandler((message: HumanMessage) => {
    if (!inChatEscalation) return;

    if (processedMessageIdsRef.current.has(message.id)) {
      return;
    }
    processedMessageIdsRef.current.add(message.id);

    const firstName = message.authorName?.split(" ")[0] ?? null;
    if (firstName && firstName !== agentName) {
      setAgentName(firstName);
    }
    if (message.authorAvatarUrl && message.authorAvatarUrl !== agentAvatarUrl) {
      setAgentAvatarUrl(message.authorAvatarUrl);
    }

    // When panel is closed, show notification instead of adding to chat
    if (!isOpenRef.current) {
      onClosedPanelMessage?.(message);
      return;
    }

    const content: string | AssistantMessageContent[] =
      message.attachments && message.attachments.length > 0
        ? buildAttachmentContent(message)
        : sanitizeIntercomBody(message.body);

    const messageId = `human-${message.id}`;

    authorRegistryRef.current.set(messageId, {
      authorName: message.authorName?.split(" ")[0] ?? null,
      authorAvatarUrl: message.authorAvatarUrl ?? null,
    });

    addMessage({ id: messageId, role: "assistant", content });
  });

  useEscalationSubscription(inChatEscalation ? handleHumanMessage : undefined);

  return { agentName, setAgentName, agentAvatarUrl, setAgentAvatarUrl };
}
